import message from 'antd/lib/message';
import Spin from 'antd/lib/spin';
import * as React from 'react';
import injectSheet from 'react-jss';
import { declareAdditionalCharges, getAdditionalChargeLines, getAdditionalChargeMaster } from 'src/api/additionalCharges';
import { FieldModel } from 'src/hooks/form/useField';
import { isValidArray } from 'src/Library/Helper';
import { StylesProps } from 'src/Library/Types';
import { ADDITIONAL_CHARGES_MESSAGES } from './AdditionalCharges.constants';
import {
  AdditionalChargeMasterOption,
  AdditionalChargesExtraData,
  ChargeRow,
} from './AdditionalCharges.types';
import AdditionalChargeDeclarationHtml from './AdditionalChargeDeclaration.html';
import styles from './AdditionalChargeDeclaration.styles';
import AdditionalChargeGrids from './AdditionalChargeGrids';
import { AdditionalChargeLinesPayload } from './AdditionalChargeGrids.types';

interface AdditionalChargeDeclarationProps extends StylesProps<ReturnType<typeof styles>> {
  field?: FieldModel<AdditionalChargesExtraData>;
}

/**
 * Which half of the one screen (parent spec §1: "two halves, one screen that flips between them") is
 * showing. `CHECKING` covers the re-entry lookup so the vendor never sees a flash of an empty Part 1
 * before landing on their already-declared Part 2.
 */
type AdditionalChargeScreen = 'CHECKING' | 'DECLARE' | 'GRID';

let rowSequence = 0;
const makeRow = (chargeCode?: string): ChargeRow => {
  rowSequence += 1;
  return { rowId: `charge-row-${rowSequence}`, chargeCode };
};

/** Declared charge codes in first-seen order, across all tabs/sections of a loaded payload. */
const chargeCodesFromPayload = (payload?: AdditionalChargeLinesPayload): string[] => {
  if (!payload) return [];
  const seen = new Set<string>();
  const codes: string[] = [];
  payload.groups.forEach((group) =>
    group.charges.forEach((charge) => {
      if (!seen.has(charge.chargeCode)) {
        seen.add(charge.chargeCode);
        codes.push(charge.chargeCode);
      }
    })
  );
  return codes;
};

/**
 * The Additional Vendor Charges task field (ISS-291748 / C1, ISS-291749 / C2) — one field that flips
 * between Part 1 (declare which charges) and Part 2 (the tabbed grids), per the parent design ("two
 * halves, one screen that flips between them" — `2026-08-10-ISS-291713-additional-vendor-charges-design.md`
 * §1).
 *
 * Re-entry is decided from data, never a client-side flag (per ISS-291749 review): on mount, `GET
 * /additional-charges/tasks/:taskId/lines` is checked first. A non-empty `groups` means the vendor
 * already declared — possibly yesterday, possibly on another device — so the field lands directly on
 * Part 2, seeded with that response. Only an empty `groups` renders Part 1.
 *
 * Part 1's "Next" calls `POST /declare` (contract §3.1) and flips to Part 2 using its response
 * directly (D9) — no follow-up `GET /lines`. `POST /declare` is idempotent against the partial-unique
 * index, so calling it again for an already-declared charge (e.g. the vendor navigates back and forward
 * without changing anything) is safe and does not duplicate lines.
 *
 * The task detail modal's own Submit remains the commit path for the field's own `{ chargeCodes }`
 * value (validator unchanged); "Next" is an additional, in-field action that progresses to Part 2 and
 * does not replace it.
 */
const AdditionalChargeDeclaration: React.FC<AdditionalChargeDeclarationProps> = ({
  classes,
  field,
}) => {
  const { vendorType: fieldVendorType, taskId } = field?.extraData || {};
  const isReadOnly = !!field?.disabled;

  const [screen, setScreen] = React.useState<AdditionalChargeScreen>('CHECKING');
  const [linesPayload, setLinesPayload] = React.useState<AdditionalChargeLinesPayload | undefined>(
    undefined
  );
  const [linesVendorType, setLinesVendorType] = React.useState<string | undefined>(undefined);
  const [isDeclaring, setIsDeclaring] = React.useState<boolean>(false);

  /**
   * `GET /lines` returns the task's `vendorType` on every 200 — including the first-visit `groups: []`
   * case — so it, not the task-detail field payload, is the authoritative source (ISS-291742). The
   * field's own `extraData.vendorType` is kept only as the fallback for the no-`taskId` path, where
   * there is nothing to call. Reporting "missing vendor type" is reserved for the case where neither
   * source produced one, never for a successful response.
   */
  const vendorType = linesVendorType || fieldVendorType;

  const [options, setOptions] = React.useState<AdditionalChargeMasterOption[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);

  /**
   * Seeded once from the stored field value — `useField` receives it synchronously via
   * `FieldNodeSingle`, so there is nothing to wait for. Rows are never re-derived from the field
   * afterwards: they are the source of truth for the value, and reading back from it would fight the
   * write below.
   */
  const [rows, setRows] = React.useState<ChargeRow[]>(() => {
    const storedCodes = field?.value?.chargeCodes;
    return isValidArray(storedCodes)
      ? (storedCodes as string[]).map((chargeCode) => makeRow(chargeCode))
      : [makeRow()];
  });

  /**
   * The one re-entry decision. `taskId` is FE-threaded, not backend-supplied (see
   * `AdditionalChargesExtraData`) — if it is ever absent, there is no way to check or to declare, so the
   * field degrades to Part 1 only (existing C1 behaviour) rather than getting stuck on a spinner.
   */
  React.useEffect(() => {
    if (!taskId) {
      setScreen('DECLARE');
      return;
    }
    let isCancelled = false;
    // `GET /lines` is the authoritative vendor-type source, so keep the loader up while it is in flight
    // — never a stale "missing vendor type" error (ISS-291749). This also matters when `taskId` threads
    // in a tick after mount: the first render had no taskId and fell to DECLARE, and without resetting to
    // CHECKING here that empty-vendorType error would stay on screen for the whole duration of the call.
    setScreen('CHECKING');
    (async () => {
      const response = await getAdditionalChargeLines({ taskId });
      if (isCancelled) return;
      if (response.isSuccess && response.data) {
        // Taken on every 200, not just the re-entry one — an empty `groups` still carries the type.
        setLinesVendorType(response.data.vendorType);
        if (response.data.groups.length > 0) {
          setLinesPayload(response.data);
          seedRowsFromCodes(chargeCodesFromPayload(response.data)); // ADD: rows for "Edit charges"
          setScreen('GRID');
          return;
        }
      }
      if (!response.isSuccess) {
        message.error(response.errorMessage || ADDITIONAL_CHARGES_MESSAGES.reentryCheckFailed);
      }
      setScreen('DECLARE');
    })();
    return () => {
      isCancelled = true;
    };
  }, [taskId]);

  /**
   * The catalog is only needed to render Part 1 — fetching it while a re-entry check is still in
   * flight, or after it has already resolved straight to Part 2, would be a wasted call.
   *
   * Keyed on `taskId`, not `vendorType`: the catalog is the shipment owner's (shipper's) config, and
   * the backend resolves the owner org + vendor type from the task (`GET /tasks/:taskId/master`). A
   * vendor is a different org, so a `vendorType`-only fetch would read the vendor's own empty catalog.
   */
  const fetchMaster = async () => {
    setIsLoading(true);
    const response = await getAdditionalChargeMaster({ taskId: taskId as string });
    if (response.isSuccess) {
      // Rendered in the order received — the backend already orders by `sequenceNumber`.
      setOptions(response.data || []);
      setErrorMessage(undefined);
    } else {
      const failure = response.errorMessage || ADDITIONAL_CHARGES_MESSAGES.fetchFailed;
      message.error(failure);
      setErrorMessage(failure);
    }
    setIsLoading(false);
  };

  React.useEffect(() => {
    if (!taskId || screen !== 'DECLARE') return;
    fetchMaster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, screen]);

  /** Mirror the declaration into the form so the task's Submit and validator see it. */
  React.useEffect(() => {
    if (isReadOnly) return;
    const chargeCodes = rows
      .map((row) => row.chargeCode)
      .filter((chargeCode): chargeCode is string => !!chargeCode);
    field?.setValue({ chargeCodes });
  }, [rows]);

  const optionsByCode = React.useMemo(
    () =>
      options.reduce((acc, option) => {
        acc[option.chargeCode] = option;
        return acc;
      }, {} as Record<string, AdditionalChargeMasterOption>),
    [options]
  );

  const getOptionByCode = (chargeCode?: string) =>
    chargeCode ? optionsByCode[chargeCode] : undefined;

  const selectedCodes = rows
    .map((row) => row.chargeCode)
    .filter((chargeCode): chargeCode is string => !!chargeCode);

  const onSelectCharge = (rowId: string, chargeCode?: string) => {
    setRows((current) =>
      current.map((row) => (row.rowId === rowId ? { ...row, chargeCode } : row))
    );
  };

  const onAddRow = () => setRows((current) => [...current, makeRow()]);

  /**
   * Re-seed Part-1 rows from an authoritative payload (the re-entry `GET /lines`), so "Edit charges"
   * after a refresh shows the already-declared charges instead of a blank row. `field.value.chargeCodes`
   * is empty on a fresh mount — the persisted truth now lives in `groups`. Guards against an empty list
   * so the picker never renders with zero rows.
   */
  const seedRowsFromCodes = (codes: string[]) =>
    setRows(codes.length ? codes.map((code) => makeRow(code)) : [makeRow()]);

  const onRemoveRow = (rowId: string) => {
    setRows((current) => {
      const remaining = current.filter((row) => row.rowId !== rowId);
      // Never leave the field empty — an empty row keeps the screen legible and the validator honest.
      return remaining.length ? remaining : [makeRow()];
    });
  };

  /**
   * Fans out the declared charges into lines server-side and hands the response straight to Part 2
   * (D9) — no follow-up `GET /lines`. A failure leaves the vendor on Part 1 with an explicit error
   * rather than flipping to an empty, broken grid.
   */
  const onNext = async () => {
    if (!taskId || !selectedCodes.length || isDeclaring) return;
    setIsDeclaring(true);
    const response = await declareAdditionalCharges({ taskId, chargeCodes: selectedCodes });
    if (response.isSuccess && response.data) {
      setLinesPayload(response.data);
      setScreen('GRID');
    } else {
      message.error(response.errorMessage || ADDITIONAL_CHARGES_MESSAGES.declareFailed);
    }
    setIsDeclaring(false);
  };

  if (screen === 'GRID') {
    return (
      <AdditionalChargeGrids
        taskId={taskId as string}
        isReadOnly={isReadOnly}
        initialLinesPayload={linesPayload}
        /* "Edit charges" returns to Part 1 so the vendor can add/remove declared charges. */
        onEditCharges={() => setScreen('DECLARE')}
        /**
         * Lets Part 2 explain an empty payload per charge (no linked BL / no containers) instead of
         * claiming nothing was declared. Only populated on the declare path — the re-entry path reaches
         * Part 2 with real groups and never needs it.
         */
        declaredCharges={
          selectedCodes
            .map((chargeCode) => getOptionByCode(chargeCode))
            .filter(Boolean) as AdditionalChargeMasterOption[]
        }
      />
    );
  }

  if (screen === 'CHECKING') {
    return (
      <div className={classes.spinner}>
        <Spin />
      </div>
    );
  }

  return AdditionalChargeDeclarationHtml({
    classes,
    isLoading,
    errorMessage,
    isReadOnly,
    vendorType,
    options,
    rows,
    selectedCodes,
    onSelectCharge,
    onAddRow,
    onRemoveRow,
    getOptionByCode,
    onNext,
    isDeclaring,
    showMissingTaskId: !isReadOnly && !!vendorType && !taskId,
  });
};

export default injectSheet(styles)(AdditionalChargeDeclaration);
